import { Switch, Route, Redirect, useHistory } from 'react-router-dom';

import AccountsList from './AccountsList';
import AccountDetailsPage from './Account/AccountDetailsPage';
import PartnerAccountCreation from './AccountCreation/PartnerAccountCreation/PartnerAccountCreation';

import BillingPackageDetails from '@/components/BillingPackages/BillingPackageDetails';

import { BillingDetailsProvider } from '@/providers/BillingDetails.provider';

import type { AccountsHandlers } from './types';
import { PendingDataProvider } from '@/providers/PendingData.provider';

import TransactionPage from '@/components/Entities/Transaction/TransactionPage';
import SignerDetailsContainer from '@/components/Accounts/Signers/SignerDetails/SignerDetailsContainer';
import AddSignercontainer from '@/components/Accounts/Signers/Addsigner/AddSignerContainer';
import EditSignerContainer from '@/components/Accounts/Signers/EditSigner/EditSignerContainer';

import R1AccountCreation from '@/components/Accounts/AccountCreation/R1AccountCreation/R1AccountCreation';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { ACCOUNTS_PATH, ACCOUNT_CREATION_PATH } from '@/components/Entities/paths';

type RoutesProps = {
  handlers: AccountsHandlers;
};

export default function Routes({ handlers }: RoutesProps) {
  const { response } = usePartnerData();
  const history = useHistory();

  const goToAccountsList = () => history.push(ACCOUNTS_PATH);

  return (
    <Switch>
      <Route path={ACCOUNTS_PATH} exact>
        <AccountsList onRouteAccess={handlers.resetAccountsState} exportCSV={handlers.exportCSV} />
      </Route>

      <Route path={ACCOUNT_CREATION_PATH}>
        <PendingDataProvider>
          {response.isInsideSales ? <R1AccountCreation /> : <PartnerAccountCreation onClose={goToAccountsList} />}
        </PendingDataProvider>
      </Route>

      <Route path={`${ACCOUNTS_PATH}/signer-create`} exact>
        <PendingDataProvider>
          <AddSignercontainer />
        </PendingDataProvider>
      </Route>

      <Route path={`${ACCOUNTS_PATH}/:accountId/view/:SigningAuthorityID`} exact>
        <SignerDetailsContainer />
      </Route>

      <Route path={`${ACCOUNTS_PATH}/:accountId/edit/:SigningAuthorityID`} exact>
        <EditSignerContainer />
      </Route>

      <Route path={`${ACCOUNTS_PATH}/:accountId/:transType/transaction/:TransactionID`}>
        <TransactionPage />
      </Route>

      <Route path={`${ACCOUNTS_PATH}/create`}>
        <PendingDataProvider>
          {response.isInsideSales ? <R1AccountCreation /> : <PartnerAccountCreation onClose={goToAccountsList} />}
        </PendingDataProvider>
      </Route>

      <Route path={`${ACCOUNTS_PATH}/billing/:resource/:id`}>
        <BillingDetailsProvider>
          <BillingPackageDetails />
        </BillingDetailsProvider>
      </Route>

      {/* Sub-accounts used to have a page of their own; the account page now serves both, so an old
          link or bookmark lands there. Only the currency carries over: the old link also carried the
          row's status and balances, which the page reads from its own request and which the embedded
          lists would otherwise read as filters. */}
      <Route
        path={`${ACCOUNTS_PATH}/sub-account/:accountId`}
        render={({ match, location }) => {
          const currency = new URLSearchParams(location.search).get('currency');

          return (
            <Redirect
              to={{
                pathname: `${ACCOUNTS_PATH}/${match.params.accountId}`,
                search: currency ? new URLSearchParams({ currency }).toString() : '',
              }}
            />
          );
        }}
      />

      {/* The tab is a path segment so the embedded lists, which write their filters to the query
          string, can never drop it. Webhooks keeps its History sub-tab one segment further down. */}
      <Route path={[`${ACCOUNTS_PATH}/:accountId/:tab(webhooks)/history`, `${ACCOUNTS_PATH}/:accountId/:tab?`]} exact>
        <AccountDetailsPage />
      </Route>
    </Switch>
  );
}
